import { useState, useEffect, useRef } from 'react';
import { GetStagedNextmvRun } from '../../../libs/GraphQL';

// Fetches the latest SUCCEEDED staged Nextmv run for a (date, fc) and exposes
// the two fields createNextmvRun needs to pre-fill: `vehicles` and
// `previousRunId` (the staged run's nextmvRunId).
//
// The query rejects when no SUCCEEDED run exists for that FC/date — we surface
// that as `error` so callers can block rerouting. Vehicles are stripped to the
// NextmvVehicleInput shape here (drop `timezone` + `__typename`) so consumers
// can pass them straight into the mutation.
//
// Bump `refreshKey` to re-fetch without changing date/fc — the confirm poller
// does this after a reroute lands, and the Route Ops heartbeat bumps it every
// tick so another supervisor's reroute converges onto this tab.
//
// A re-fetch failure for the SAME (date, fc) after a prior success keeps the
// last-known-good data instead of clearing: a SUCCEEDED run can't cease to
// exist, so wiping state on a transient network blip would falsely block
// rerouting. Failures with no prior success (first load, or right after a
// date/fc change) still clear state and surface `error` as before.

function toVehicleInput(vehicle) {
  const { timezone, __typename, ...rest } = vehicle;
  return rest;
}

export default function useStagedNextmvRun(
  dateStr,
  fulfillmentCenter,
  refreshKey = 0,
) {
  const [state, setState] = useState({
    vehicles: [],
    previousRunId: null,
    isLoading: false,
    error: null,
  });
  // Key of the last (date, fc) whose fetch succeeded — the catch branch
  // compares against it to tell "refresh blip" from "no run for this key".
  const lastGoodKeyRef = useRef(null);

  useEffect(() => {
    if (!dateStr || !fulfillmentCenter) {
      lastGoodKeyRef.current = null;
      setState({
        vehicles: [],
        previousRunId: null,
        isLoading: false,
        error: null,
      });
      return undefined;
    }

    const key = `${dateStr}|${fulfillmentCenter}`;
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    GetStagedNextmvRun({ dropOffDate: dateStr, fulfillmentCenter })
      .then((staged) => {
        if (cancelled) return;
        lastGoodKeyRef.current = key;
        setState({
          vehicles: (staged.vehicles || []).map(toVehicleInput),
          previousRunId: staged.nextmvRunId || null,
          isLoading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (lastGoodKeyRef.current === key) {
          // Background refresh failed but we already hold good data for this
          // key — keep it. Same silent-log policy as the heartbeat.
          console.error(
            '[useStagedNextmvRun] refresh failed; keeping last-known-good:',
            err,
          );
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }
        lastGoodKeyRef.current = null;
        setState({
          vehicles: [],
          previousRunId: null,
          isLoading: false,
          error: err || new Error('No staged run'),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [dateStr, fulfillmentCenter, refreshKey]);

  return state;
}
